import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AdminBookEditorService } from '../../../shared/services/apis/platform/admin-book-editor.service';
import { AdminBookEditorListRequest } from '../../../shared/services/requests/platform/admin-book-editor.request';
import { MyBookItemStruct, MyBookVersionStruct } from '../../../shared/services/structs/my-books.struct';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { AdminBookEditorMarketplaceToggleRequest } from '../../../shared/services/requests/platform/admin-book-editor.request';
import { AdminBookEditorMarketplaceResponse } from '../../../shared/services/responses/platform/admin-book-editor.response';
import { MarketplaceListingStatusEnum } from '../../../shared/services/enums/marketplace.enum';

@Component({
    selector: 'app-admin-book-editor',
    standalone: true,
    templateUrl: './admin-book-editor.component.html',
    styleUrl: './admin-book-editor.component.css',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MenuComponent,
        FooterComponent,
        LoginValidationComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatDialogModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminBookEditorComponent implements OnInit {
    isLoading = false;

    form!: FormGroup;

    readonly statusOptions = [
        { label: 'Todos', value: null },
        { label: 'Em edição', value: 'Em edição' },
        { label: 'Aguarda pagamento', value: 'Aguarda pagamento' },
        { label: 'Em revisão', value: 'Em revisão' },
        { label: 'Aguarda ajustes', value: 'Aguarda ajustes' },
        { label: 'Aguarda aprovação', value: 'Aguarda aprovação' },
        { label: 'Publicado', value: 'Publicado' },
        { label: 'Suspenso', value: 'Suspenso' },
    ];

    list: MyBookItemStruct[] = [];
    page = 1;
    readonly pageSize = 100;
    total = 0;

    constructor(
        private fb: FormBuilder,
        private editorService: AdminBookEditorService,
        private alertService: AlertService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialog: MatDialog
    ) { }

    /** Versões com toggle de marketplace em processamento (evita clique duplo). */
    togglingMarketplace = new Set<number>();

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.form = this.fb.group({
            search: [''],
            status: [null],
            dateStart: [null],
            dateEnd: [null],
        });
    }

    onLoginValidated(isLogged: boolean) {
        if (!isLogged) return;
        this.fetch();
    }

    fetch(): void {
        this.isLoading = true;

        const req: AdminBookEditorListRequest = {
            page: this.page,
            pageSize: this.pageSize,
            search: (this.form.value.search || '').trim() || null,
            status: this.form.value.status || null,
            dateStart: this.toIsoOrNull(this.form.value.dateStart),
            dateEnd: this.toIsoOrNull(this.form.value.dateEnd),
        };

        this.editorService.listBooks(req).subscribe({
            next: (res) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao carregar livros.', AlertType.error);
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return;
                }

                const PLACEHOLDER = 'assets/images/book-placeholder.svg';
                this.list = (res.list ?? []).map(item => ({
                    ...item,
                    versions: item.versions?.map(v => ({
                        ...v,
                        coverUrl: (v.coverUrl && v.coverUrl.length ? v.coverUrl : PLACEHOLDER)
                    })) ?? []
                }));

                this.total = res.total ?? this.list.length;
                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.alertService.show('Erro', 'Erro inesperado ao carregar a lista.', AlertType.error);
                this.isLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    applyFilters(): void {
        this.page = 1;
        this.fetch();
    }

    resetFilters(): void {
        this.form.reset({ search: '', status: null, dateStart: null, dateEnd: null });
        this.page = 1;
        this.fetch();
    }

    prevPage(): void {
        if (this.page <= 1) return;
        this.page--;
        this.fetch();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    nextPage(): void {
        if (this.page >= this.totalPages) return;
        this.page++;
        this.fetch();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.total / this.pageSize));
    }

    get pageRangeStart(): number {
        return (this.page - 1) * this.pageSize + 1;
    }

    get pageRangeEnd(): number {
        return Math.min(this.page * this.pageSize, this.total);
    }

    onImgError(ev: Event) {
        const el = ev?.target as HTMLImageElement;
        if (el) el.src = 'assets/images/book-placeholder.png';
    }

    trackByVersion = (_: number, v: any) => v?.idBookVersion ?? _;
    trackByBook = (_: number, b: any) => b?.idBook ?? _;

    goTo(idBookVersion: number) {
        if (!idBookVersion) return;
        this.router.navigateByUrl(`/editor-admin/${idBookVersion}`);
    }

    // =========================================================
    // Marketplace (Amazon) — toggle "Liberado para marketplace"
    // =========================================================

    /** Sem os pré-requisitos só é possível desligar; com remoção pendente, nada até concluir. */
    isMarketplaceToggleDisabled(v: MyBookVersionStruct): boolean {
        const m = v.marketplace;
        if (!m || this.togglingMarketplace.has(v.idBookVersion)) return true;
        if (m.isRemovalPending && !m.isMarketplaceEnabled) return true;
        return !m.isMarketplaceEnabled && !m.canEnable;
    }

    marketplaceTooltip(v: MyBookVersionStruct): string {
        const m = v.marketplace;
        if (!m) return '';
        if (m.isRemovalPending && !m.isMarketplaceEnabled) return 'Aguardando a remoção do anúncio na Amazon.';
        if (!m.isMarketplaceEnabled && !m.canEnable) return 'Para liberar: ' + m.enableBlockReasons.join(' ');
        if (m.lastError) return 'Erro: ' + m.lastError;
        if (m.externalId) return `Publicado na Amazon (ASIN ${m.externalId}).`;
        return m.isMarketplaceEnabled ? 'Liberado para publicação na Amazon.' : 'Clique para liberar a publicação na Amazon.';
    }

    marketplaceChipClass(v: MyBookVersionStruct): string {
        switch (v.marketplace?.idListingStatus) {
            case MarketplaceListingStatusEnum.Publicado: return 'chip-mk chip-mk-success';
            case MarketplaceListingStatusEnum.Erro: return 'chip-mk chip-mk-warn';
            case MarketplaceListingStatusEnum.Removido: return 'chip-mk chip-mk-off';
            case MarketplaceListingStatusEnum.Publicando: return 'chip-mk chip-mk-info';
            default: return 'chip-mk chip-mk-pending';
        }
    }

    onToggleMarketplace(v: MyBookVersionStruct, ev: MatSlideToggleChange): void {
        const enabled = ev.checked;
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: enabled ? 'Liberar publicação na Amazon' : 'Desativar publicação na Amazon',
                description: enabled
                    ? `O livro "${v.title}" será publicado na Amazon com o preço do kit de livro físico. ` +
                      'A partir deste momento os dados do livro ficam bloqueados para edição. Deseja continuar?'
                    : `O anúncio do livro "${v.title}" será removido da Amazon. A edição será liberada após a remoção. Deseja continuar?`,
                buttonConfirm: enabled ? 'Sim, liberar' : 'Sim, desativar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) {
                ev.source.checked = !enabled;
                this.cdr.markForCheck();
                return;
            }

            const req = new AdminBookEditorMarketplaceToggleRequest();
            req.idBookVersion = v.idBookVersion;
            req.enabled = enabled;

            this.togglingMarketplace.add(v.idBookVersion);
            this.cdr.markForCheck();

            this.editorService.toggleMarketplace(req).subscribe({
                next: (res: AdminBookEditorMarketplaceResponse) => {
                    this.togglingMarketplace.delete(v.idBookVersion);
                    if (res?.isError) {
                        ev.source.checked = !enabled;
                        this.alertService.show('Amazon', res.errorDescription || 'Não foi possível alterar a publicação.', AlertType.error);
                    } else {
                        this.applyMarketplaceResponse(v, res);
                        this.alertService.show('Amazon', res.message || 'Publicação atualizada.', AlertType.success);
                    }
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.togglingMarketplace.delete(v.idBookVersion);
                    ev.source.checked = !enabled;
                    this.alertService.show('Erro', 'Falha ao alterar a publicação na Amazon.', AlertType.error);
                    this.cdr.markForCheck();
                },
            });
        });
    }

    /** Atualiza o resumo da linha com o estado devolvido pela API. */
    private applyMarketplaceResponse(v: MyBookVersionStruct, res: AdminBookEditorMarketplaceResponse): void {
        const m = res.marketplace;
        if (!m) return;
        const listing = [...(m.listings ?? [])].sort((a, b) => a.idMarketplace - b.idMarketplace)[0];
        v.marketplace = {
            isMarketplaceEnabled: m.isMarketplaceEnabled,
            canEnable: m.canEnable,
            enableBlockReasons: m.enableBlockReasons ?? [],
            idListingStatus: listing?.idListingStatus ?? null,
            listingStatusName: listing?.listingStatusName ?? null,
            externalId: listing?.externalId ?? null,
            lastError: listing?.lastError ?? null,
            isRemovalPending: listing?.idListingStatus === MarketplaceListingStatusEnum.RemocaoPendente,
        };
    }

    private toIsoOrNull(date: Date | null): string | null {
        if (!date) return null;
        return new Date(date).toISOString();
    }
}
